// S14 — Settings types (admin AppSetting + public subset).
//
// We model the BE `AppSettingDto` directly. Legacy `SystemSettings` /
// `UpdateSystemSettingsInput` are kept as adapter types for the settingsApi
// legacy shim. NOTE: aktif tüketici kalmadı (HelpPage kaldırıldı).

export type SettingValueType = 'String' | 'Int' | 'Bool' | 'Json';

export const SETTING_VALUE_TYPE_TO_NUMBER: Record<SettingValueType, number> = {
  String: 0,
  Int: 1,
  Bool: 2,
  Json: 3,
};

// The BE serializes the enum as its string name ("Int", "Bool", …) on read but accepts the
// numeric form on write, so this must tolerate both (and numeric-strings, defensively).
export function settingValueTypeFromNumber(v: number | string | undefined): SettingValueType {
  if (typeof v === 'string') {
    switch (v.trim().toLowerCase()) {
      case 'int':
      case '1':
        return 'Int';
      case 'bool':
      case '2':
        return 'Bool';
      case 'json':
      case '3':
        return 'Json';
      default:
        return 'String';
    }
  }
  switch (v) {
    case 1:
      return 'Int';
    case 2:
      return 'Bool';
    case 3:
      return 'Json';
    default:
      return 'String';
  }
}

export interface AppSetting {
  id: string;
  key: string;
  value: string;
  valueType: SettingValueType;
  category: string;
  isPublic: boolean;
  description: string | null;
  updatedAt: string;
  rowVersion: string | null;
}

export interface CreateSettingInput {
  key: string;
  value: string;
  valueType: SettingValueType;
  category: string;
  isPublic: boolean;
  description?: string | null;
}

export interface UpdateSettingInput {
  value: string;
  valueType: SettingValueType;
  category: string;
  isPublic: boolean;
  description?: string | null;
  rowVersion?: string | null;
}

export interface PublicSetting {
  key: string;
  value: string;
  valueType: SettingValueType;
  category: string;
}

// ---- Legacy shape (read-only) — derived from BE keys when needed --------
export interface SystemSettings {
  siteName: string;
  contactEmail: string;
  defaultLocale: string;
  timezone: string;
  brandColor: string;
  logoUrl: string | null;
  faviconUrl: string | null;
  maintenanceMode: boolean;
  registrationOpen: boolean;
  forumEnabled: boolean;
  defaultPageSize: number;
  privacyPolicyPageId: string | null;
  termsPageId: string | null;
  updatedAt: string;
  updatedBy: string;
}

export interface UpdateSystemSettingsInput {
  [key: string]: unknown;
}

/** Sensitive keys default to confirm-modal before save and masked display. */
export const SENSITIVE_KEY_PATTERNS = [
  /secret/i,
  /password/i,
  /apikey/i,
  /api_key/i,
  /\.key$/i,
  /token/i,
  /connectionstring/i,
];

export function isSensitiveKey(key: string): boolean {
  return SENSITIVE_KEY_PATTERNS.some((re) => re.test(key));
}

/** Group key prefix → group label. Falls back to category from BE. */
export function categoryOf(setting: AppSetting): string {
  if (setting.category && setting.category.trim()) return setting.category;
  const dot = setting.key.indexOf('.');
  if (dot > 0) return setting.key.slice(0, dot);
  return 'general';
}
